import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);

    if (this.#isClickTrigger) {
      document.addEventListener('click', this.#documentClickListener);
      document.addEventListener('keydown', this.#documentKeydownListener);
    } else {
      this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);

    if (this.#isClickTrigger) {
      document.removeEventListener('click', this.#documentClickListener);
      document.removeEventListener('keydown', this.#documentKeydownListener);
    } else {
      this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    }

    this.#cleanupMutationObserver();
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #documentClickListener = (event) => {
    if (!(event.target instanceof Node)) return;

    const path = event.composedPath();
    if (path.includes(this)) return;

    this.#deactivate(undefined, { force: true });
  };

  #documentKeydownListener = (event) => {
    if (event.key !== 'Escape') return;

    this.#deactivate(undefined, { force: true });
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  get #isClickTrigger() {
    return this.dataset.menuTrigger === 'click';
  }

  /**
   * @param {Node} node
   * @returns {boolean}
   */
  #isNodeInsideMenu(node) {
    if (this.contains(node)) return true;

    return isNodeWithinElement(this, node);
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Toggle submenu visibility on click (desktop click-trigger mode).
   * @param {MouseEvent} event
   */
  handleClick = (event) => {
    this.#toggleMenuFromInteraction(event);
  };

  /**
   * Toggle submenu visibility via keyboard (Enter / Space).
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    this.#toggleMenuFromInteraction(event);
  };

  /**
   * @param {MouseEvent | KeyboardEvent} event
   */
  #toggleMenuFromInteraction(event) {
    if (!(event.target instanceof Element)) return;

    // The handler is attached to the whole <li>, which also contains the submenu.
    // Clicks originating inside the submenu must navigate normally, not toggle the menu.
    const originalTarget = event.composedPath?.()[0] ?? event.target;
    if (originalTarget instanceof Element && originalTarget.closest('[ref="submenu[]"]')) return;

    const listItem = event.target;
    const isMoreItem = listItem.matches('[slot="more"]');
    const menuItem = findMenuItem(listItem);

    if (!menuItem && !isMoreItem) return;

    const hasSubmenu = menuItem?.hasAttribute('aria-haspopup') || isMoreItem;

    if (hasSubmenu) {
      event.preventDefault();
    } else {
      return;
    }

    if (menuItem === this.#state.activeItem) {
      this.#deactivate(undefined, { force: true });
      return;
    }

    this.activate(event);
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              // Update both variables together, otherwise the clip-path bottom edge
              // (driven by --full-open-header-height) cuts off the extra content
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#setFullOpenHeaderHeight(submenu.offsetHeight);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);

      // Track late height changes (image/font loading) that don't mutate the DOM.
      // Only for regular submenus; the overflow menu uses different height math.
      this.#submenuResizeObserver?.disconnect();
      if (isDefaultSlot) {
        const observedSubmenu = submenu;
        this.#submenuResizeObserver = new ResizeObserver(() => {
          if (this.#state.activeItem !== item) return;

          const height = observedSubmenu.offsetHeight;
          if (height > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
            this.#setFullOpenHeaderHeight(height);
          }
        });
        this.#submenuResizeObserver.observe(observedSubmenu);
      }
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (this.#isClickTrigger && event.type === 'blur') {
      // Only close when focus provably moved outside the menu. A null relatedTarget
      // (e.g. mousedown on a submenu link mid-click) must not close the menu;
      // the document click listener handles clicks outside.
      if (!(event.relatedTarget instanceof Node) || this.#isNodeInsideMenu(event.relatedTarget)) {
        return;
      }
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (
      !options.force &&
      !this.#isClickTrigger &&
      (this.overflowListHovered || this.overflowMenu?.matches(':hover'))
    ) {
      return;
    }

    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Check whether a node is inside an element, including shadow DOM trees.
 * @param {HTMLElement} root
 * @param {Node} node
 * @returns {boolean}
 */
function isNodeWithinElement(root, node) {
  if (!(node instanceof Node)) return false;

  /** @type {Node | null} */
  let current = node;

  while (current) {
    if (current === root) return true;

    if (current instanceof ShadowRoot) {
      current = current.host;
      continue;
    }

    current = current.parentNode;
  }

  return false;
}
